import { OPCOES_FILTRO_DATA } from '@constants/Text'
import {
  MODO_TO_OPTIONS_FIELD_KEY,
  buildPrimaryDropdownItems,
} from '@modules/consolidator/utils/wallet-modo-configs/wallet-modo-configs'
import { isLabeledOptionsField } from '@modules/consolidator/utils/build-wallet-options-fields/build-wallet-options-fields'

const FIELDS_WITH_ASYNC_ITEMS = new Set([
  'filtroCorretora',
  'tipo',
  'subtipo',
  'classe',
  'subclasse',
  'setor',
  'emissor',
  'idConta',
  'tags',
  'corretora',
  'nome_portfolio',
])

const MULTISELECT_FIELDS = new Set([
  'filtroCorretora',
  'tipo',
  'subtipo',
  'classe',
  'subclasse',
  'setor',
  'emissor',
  'idConta',
  'tags',
  'corretora',
  'nome_portfolio',
])

export const hasAsyncItems = (field) => FIELDS_WITH_ASYNC_ITEMS.has(field.key)

export const isMultiselect = (field) => MULTISELECT_FIELDS.has(field.key)

export const WALLET_STANDALONE_FILTER_KEYS = ['corretora', 'nome_portfolio']

export const resolveStandaloneFilterKeys = (modo) =>
  WALLET_STANDALONE_FILTER_KEYS.filter((key) => key !== modo)

export const WALLET_FIELD_DEFAULTS = {
  modoFiltroCorretora: {
    label: 'Agrupar por',
    default: 'subclasse',
    itemText: 'text',
    itemValue: 'value',
  },
  filtroCorretora: {
    label: 'Instituição',
    default: [],
  },
  filtroData: {
    label: 'Data',
    items: Object.values(OPCOES_FILTRO_DATA).filter(
      (v) => v !== OPCOES_FILTRO_DATA.ESPECIFICA
    ),
    default: '12 Meses',
  },
  investmentTypeFilter: {
    label: 'Tipo de investimento',
    items: ['Todos', 'Onshore', 'Offshore'],
    default: 'Todos',
  },
  tipo: { default: [] },
  subtipo: { default: [] },
  classe: { default: [] },
  subclasse: { default: [] },
  setor: { default: [] },
  emissor: { default: [] },
  idConta: { default: [] },
  tags: { default: [] },
  corretora: { default: [] },
  nome_portfolio: { default: [] },
}

export const buildDefaultWalletFiltersData = () => ({
  modoFiltroCorretora: WALLET_FIELD_DEFAULTS.modoFiltroCorretora.default,
  filtroCorretora: [],
  filtroData: WALLET_FIELD_DEFAULTS.filtroData.default,
  lastValidFiltroData: WALLET_FIELD_DEFAULTS.filtroData.default,
  investmentTypeFilter: WALLET_FIELD_DEFAULTS.investmentTypeFilter.default,
  dataInicio: null,
  dataFim: null,
  tipo: [],
  subtipo: [],
  classe: [],
  subclasse: [],
  setor: [],
  emissor: [],
  idConta: [],
  tags: [],
  instituicao: [],
  portfolio: [],
  userChangedFields: {},
})

export const FIELD_KEY_TO_GETTER = {
  modoFiltroCorretora: 'consolidator/walletFiltersModo',
  filtroCorretora: 'consolidator/walletFiltersCorretora',
  filtroData: 'consolidator/walletFiltersData',
  investmentTypeFilter: 'consolidator/walletFiltersInvestmentType',
  tipo: 'consolidator/walletFiltersTipo',
  subtipo: 'consolidator/walletFiltersSubtipo',
  classe: 'consolidator/walletFiltersClasse',
  subclasse: 'consolidator/walletFiltersSubclasse',
  setor: 'consolidator/walletFiltersSetor',
  emissor: 'consolidator/walletFiltersEmissor',
  idConta: 'consolidator/walletFiltersIdConta',
  tags: 'consolidator/walletFiltersTags',
  corretora: 'consolidator/walletFiltersInstituicao',
  nome_portfolio: 'consolidator/walletFiltersPortfolio',
}

export const FIELD_KEY_TO_ACTION = {
  modoFiltroCorretora: 'consolidator/setWalletFiltersModo',
  filtroCorretora: 'consolidator/setWalletFiltersCorretora',
  filtroData: 'consolidator/setWalletFiltersData',
  investmentTypeFilter: 'consolidator/setWalletFiltersInvestmentType',
  tipo: 'consolidator/setWalletFiltersTipo',
  subtipo: 'consolidator/setWalletFiltersSubtipo',
  classe: 'consolidator/setWalletFiltersClasse',
  subclasse: 'consolidator/setWalletFiltersSubclasse',
  setor: 'consolidator/setWalletFiltersSetor',
  emissor: 'consolidator/setWalletFiltersEmissor',
  idConta: 'consolidator/setWalletFiltersIdConta',
  tags: 'consolidator/setWalletFiltersTags',
  corretora: 'consolidator/setWalletFiltersInstituicao',
  nome_portfolio: 'consolidator/setWalletFiltersPortfolio',
}

export const resolveWalletFilterFields = ({
  fields,
  store,
  dynamicLabels = {},
}) => {
  if (!Array.isArray(fields)) return []
  return fields.map((field) =>
    resolveSingleField({ field, store, dynamicLabels })
  )
}

const resolveSingleField = ({ field, store, dynamicLabels }) => {
  const fieldKey = typeof field === 'string' ? field : field.key
  const defaults = WALLET_FIELD_DEFAULTS[fieldKey] || {}
  const overrides = typeof field === 'object' ? field : {}
  const isMulti = MULTISELECT_FIELDS.has(fieldKey)
  const resolved = {
    ...defaults,
    ...overrides,
    key: fieldKey,
    multiple: overrides.multiple === undefined ? isMulti : overrides.multiple,
  }
  if (!overrides.label && dynamicLabels[fieldKey]) {
    resolved.label = dynamicLabels[fieldKey]
  }
  applyDynamicItems({ resolved, fieldKey, overrides, store })
  return resolved
}

const applyDynamicItems = ({ resolved, fieldKey, overrides, store }) => {
  if (fieldKey === 'filtroCorretora') {
    resolved.items = resolveCorretoraItems({ overrides, store })
    applyCorretoraItemShape({ resolved, store })
    return
  }
  if (fieldKey === 'modoFiltroCorretora' && !overrides.items) {
    resolved.items = resolveModoItems(store)
  }
}

const applyCorretoraItemShape = ({ resolved, store }) => {
  const optionsFieldKey = resolveOptionsFieldKeyByModo(store)
  const sourceField = resolveOptionsFieldByModo(store)
  const itemText = sourceField && sourceField.itemText
  const itemValue = sourceField && sourceField.itemValue
  if (itemText) resolved.itemText = itemText
  if (itemValue) resolved.itemValue = itemValue
  if (!isLabeledOptionsField(optionsFieldKey)) return
  if (!resolved.itemText) resolved.itemText = 'label'
  if (!resolved.itemValue) resolved.itemValue = 'id'
}

const resolveModoItems = (store) => {
  const optionsFields =
    store.getters['consolidator/walletAnalysisOptionsFields'] || []
  return buildPrimaryDropdownItems(optionsFields)
}

export const applyWalletFilterDefaults = ({ fields, store }) => {
  const resolved = resolveWalletFilterFields({ fields, store })
  const userChanged =
    store.getters['consolidator/walletFiltersUserChangedField']
  const coercedKeys = new Set()
  for (const field of resolved) {
    const isUserChanged =
      typeof userChanged === 'function' && userChanged(field.key)
    applyDefaultForField({ field, store, isUserChanged, coercedKeys })
  }
}

const resolveCorretoraItems = ({ overrides, store }) => {
  const prepend = overrides.prepend || []
  const append = overrides.append || []
  const rawItems = resolveItemsByModo(store)
  const items = rawItems.filter((item) => item !== null && item !== undefined)
  return [...prepend, ...items, ...append]
}

const resolveItemsByModo = (store) => {
  const field = resolveOptionsFieldByModo(store)
  if (!field || !Array.isArray(field.items)) return []
  return field.items
}

const resolveOptionsFieldKeyByModo = (store) => {
  const modo = store.getters['consolidator/walletFiltersModo']
  return MODO_TO_OPTIONS_FIELD_KEY[modo] || modo
}

const resolveOptionsFieldByModo = (store) => {
  const optionsFieldKey = resolveOptionsFieldKeyByModo(store)
  const fields = store.getters['consolidator/walletAnalysisOptionsFields'] || []
  return fields.find((field) => field.key === optionsFieldKey) || null
}

const resolveItemValues = (items) => {
  if (!Array.isArray(items)) return []
  return items.map((item) =>
    typeof item === 'object' && item !== null ? item.value : item
  )
}

const resolveDefault = ({ field, items }) => {
  if (field.default) return field.default
  if (items.length > 0) return items[0]
  return ''
}

const dispatchLastValidWhenApplicable = ({ field, value, store }) => {
  if (field.key !== 'filtroData') return
  if (value === OPCOES_FILTRO_DATA.ESPECIFICA) return
  store.dispatch('consolidator/setWalletFiltersLastValidData', value)
}

const applyUntouchedDefault = ({ field, action, getter, store }) => {
  if (field.default === undefined) return
  const currentValue = store.getters[getter]
  if (areEquivalent(currentValue, field.default)) return
  store.dispatch(action, field.default)
  dispatchLastValidWhenApplicable({ field, value: field.default, store })
}

const areEquivalent = (a, b) => {
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false
    return a.every((item, index) => item === b[index])
  }
  return a === b
}

const cascadeResetFromCoercedModo = ({ field, action, getter, store }) => {
  const items = field.items || []
  const prepend = field.prepend || []
  const append = field.append || []
  const hasLoadedAsyncItems = items.some(
    (item) => !prepend.includes(item) && !append.includes(item)
  )
  if (!hasLoadedAsyncItems) return
  const currentValue = store.getters[getter]
  if (items.includes(currentValue)) return
  if (field.default === undefined) return
  store.dispatch(action, field.default)
  store.dispatch('consolidator/setWalletFiltersUserChangedField', {
    key: field.key,
    value: false,
  })
}

const coerceSyncItemsField = ({
  field,
  action,
  getter,
  store,
  coercedKeys,
}) => {
  const items = resolveItemValues(field.items)
  if (items.length === 0) return
  const currentValue = store.getters[getter]
  if (items.includes(currentValue)) return
  const defaultValue = resolveDefault({ field, items })
  store.dispatch(action, defaultValue)
  dispatchLastValidWhenApplicable({ field, value: defaultValue, store })
  store.dispatch('consolidator/setWalletFiltersUserChangedField', {
    key: field.key,
    value: false,
  })
  coercedKeys.add(field.key)
}

const coerceTouchedField = ({ field, action, getter, store, coercedKeys }) => {
  if (!hasAsyncItems(field)) {
    coerceSyncItemsField({ field, action, getter, store, coercedKeys })
    return
  }
  if (coercedKeys.has('modoFiltroCorretora')) {
    cascadeResetFromCoercedModo({ field, action, getter, store })
  }
}

const applyDefaultForField = ({ field, store, isUserChanged, coercedKeys }) => {
  const getter = FIELD_KEY_TO_GETTER[field.key]
  const action = FIELD_KEY_TO_ACTION[field.key]
  if (!getter || !action) return
  if (!isUserChanged) {
    applyUntouchedDefault({ field, action, getter, store })
    return
  }
  coerceTouchedField({ field, action, getter, store, coercedKeys })
}

export const restoreIncompleteSpecificDate = ({ fields, store }) => {
  const filtroData = store.getters['consolidator/walletFiltersData']
  if (filtroData !== OPCOES_FILTRO_DATA.ESPECIFICA) return

  const dataInicio = store.getters['consolidator/walletFiltersDataInicio']
  const dataFim = store.getters['consolidator/walletFiltersDataFim']
  if (dataInicio || dataFim) return

  const lastValid = store.getters['consolidator/walletFiltersLastValidData']
  const resolved = resolveWalletFilterFields({ fields, store })
  const filtroDataField = resolved.find((f) => f.key === 'filtroData')
  const fallback = filtroDataField ? filtroDataField.default : ''
  const restoreValue = lastValid || fallback

  store.dispatch('consolidator/setWalletFiltersData', restoreValue)
}
